import {
  ColumnDef,
  PaginationState,
  Row,
  SortingState,
} from '@tanstack/react-table'
import { CantileverButton } from 'components/CantileverButton'
import { ConfirmDeletePopup } from 'components/ConfirmDeletePopup'
import { TextCell } from 'components/table/cells/TextCell'
import { TableComponent } from 'components/table/TableComponent'
import { EditDetectionConfigurationDataContext } from 'features/anomalyDetection/contexts/EditDetectionConfigurationDataLayer'
import { EditDetectionConfigurationUXContext } from 'features/anomalyDetection/contexts/EditDetectionConfigurationUXLayer'
import { X } from 'lucide-react'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import { DetectionModel, InputType } from 'types'

const RemoveButtonCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledX = styled(X)`
  height: 1rem;
  width: 1rem;
  color: ${({ theme }) => theme.forecast.danger};
`

interface DetectionModelTableProps {
  onRowClick: (index: number) => void
}

export const DetectionModelTable: React.FC<DetectionModelTableProps> = ({
  onRowClick,
}) => {
  const {
    detectionConfiguration: { detection_models: detectionModels },
  } = useContext(EditDetectionConfigurationDataContext)
  const { handleSaveToRolodex } = useContext(
    EditDetectionConfigurationUXContext
  )
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  })
  const [sorting, setSorting] = useState<SortingState>([])
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [deletePopupPosition, setDeletePopupPosition] = useState({
    top: 0,
    left: 0,
  })
  const [modelIndexToDelete, setModelIndexToDelete] = useState<number | null>(
    null
  )

  const handleDeleteClick = useCallback(
    (index: number) => (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      const rect = event.currentTarget.getBoundingClientRect()
      setDeletePopupPosition({
        top: rect.bottom + 4,
        left: rect.left + 16,
      })
      setModelIndexToDelete(index)
      setShowConfirmDelete(true)
    },
    []
  )

  const confirmDelete = () => {
    if (modelIndexToDelete === null) return

    const updatedModels = detectionModels.filter(
      (_, index) => index !== modelIndexToDelete
    )

    handleSaveToRolodex({
      detection_models: updatedModels,
    })

    setShowConfirmDelete(false)
    setModelIndexToDelete(null)
  }

  const cancelDelete = () => {
    setShowConfirmDelete(false)
    setModelIndexToDelete(null)
  }

  const handleRowClick = (row: Row<DetectionModel>) => {
    onRowClick(row.index)
  }

  const columns = useMemo<ColumnDef<DetectionModel>[]>(
    () => [
      {
        id: 'remove',
        header: '',
        cell: ({ row }) => (
          <RemoveButtonCell>
            <CantileverButton iconOnly onClick={handleDeleteClick(row.index)}>
              <StyledX />
            </CantileverButton>
          </RemoveButtonCell>
        ),
      },
      {
        accessorKey: 'model_name',
        id: 'modelName',
        header: 'Model Name',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        enableSorting: true,
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => <TextCell text={row.original.model_name} />,
      },
      {
        accessorKey: 'is_enabled',
        id: 'isEnabled',
        header: 'Enabled',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        enableSorting: true,
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => (
          <TextCell text={row.original.is_enabled ? 'Yes' : 'No'} />
        ),
      },
      {
        accessorKey: 'model_parameters',
        id: 'modelParameters',
        header: 'Parameters',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        enableSorting: false,
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => (
          <TextCell
            truncate={true}
            text={Object.entries(row.original.model_parameters)
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ')}
          />
        ),
      },
    ],
    [handleDeleteClick]
  )

  return (
    <>
      <TableComponent
        data={detectionModels}
        isLoadingData={false}
        columns={columns}
        footerName='Detection Models'
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        onRowClick={handleRowClick}
        showFilterBar={false}
        showTable={true}
      />
      {showConfirmDelete && (
        <ConfirmDeletePopup
          entityName='detection model'
          deleteMessage='Are you sure you want to delete this detection model?'
          popupPosition={deletePopupPosition}
          cancelDelete={cancelDelete}
          confirmDelete={confirmDelete}
        />
      )}
    </>
  )
}
